const config = {
  backendBaseUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080',
  accessTokenKey: 'neopass_user_access_token',
  refreshTokenKey: 'neopass_user_refresh_token',
};

class UserApiService {
  constructor() {
    this.baseUrl = config.backendBaseUrl;
  }

  getAccessToken() {
    return localStorage.getItem(config.accessTokenKey);
  }

  getRefreshToken() {
    return localStorage.getItem(config.refreshTokenKey);
  }

  setTokens(accessToken, refreshToken) {
    if (accessToken) {
      localStorage.setItem(config.accessTokenKey, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(config.refreshTokenKey, refreshToken);
    }
  }

  clearTokens() {
    localStorage.removeItem(config.accessTokenKey);
    localStorage.removeItem(config.refreshTokenKey);
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        return null;
      }

      const data = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return data.accessToken;
    } catch (error) {
      this.clearTokens();
      return null;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = this.getAccessToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
      let response = await fetch(url, { ...options, headers });

      if (response.status === 401 && this.getRefreshToken()) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          response = await fetch(url, { ...options, headers });
        }
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `Request failed: ${response.status}`);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // API Methods
  async getAccount() {
    return this.get('/api/account');
  }

  async purchaseCredits(tokenAmount) {
    return this.post('/api/account/purchase', { tokenAmount });
  }

  // ========== Razorpay Payment Methods ==========
  
  /**
   * Creates a Razorpay order for payment
   * @param {Object} orderData - Order details
   * @param {number} orderData.amount - Amount in paise (₹1 = 100 paise)
   * @param {string} orderData.creditType - Type of credits ('mcq' or 'coding')
   * @param {number} orderData.creditAmount - Number of credits to purchase
   * @returns {Promise<Object>} Order response with orderId, keyId, etc.
   */
  async createPaymentOrder(orderData) {
    return this.post('/api/payment/create-order', {
      amount: orderData.amount,
      currency: 'INR',
      creditType: orderData.creditType,
      creditAmount: orderData.creditAmount,
    });
  }

  /**
   * Verifies Razorpay payment after successful checkout
   * @param {Object} paymentData - Payment verification details
   * @param {string} paymentData.razorpayOrderId - Order ID from Razorpay
   * @param {string} paymentData.razorpayPaymentId - Payment ID from Razorpay
   * @param {string} paymentData.razorpaySignature - Signature for verification
   * @param {string} paymentData.creditType - Type of credits purchased
   * @param {number} paymentData.creditAmount - Number of credits purchased
   * @returns {Promise<Object>} Verification response with success status and new balance
   */
  async verifyPayment(paymentData) {
    return this.post('/api/payment/verify-payment', {
      razorpayOrderId: paymentData.razorpayOrderId,
      razorpayPaymentId: paymentData.razorpayPaymentId,
      razorpaySignature: paymentData.razorpaySignature,
      creditType: paymentData.creditType,
      creditAmount: paymentData.creditAmount,
    });
  }

  /**
   * Gets Razorpay configuration (public key ID)
   * @returns {Promise<Object>} Payment config with keyId
   */
  async getPaymentConfig() {
    return this.get('/api/payment/config');
  }
}

const userApi = new UserApiService();
export default userApi;
